/**
 * SSE hook for the API sequence doctor agent.
 *
 * @module FlowDesigner/api/hooks/useApiSequenceDoctor
 */

import { useCallback, useRef, useState } from 'react';
import { API_URL } from '../../../../../config';
import type { ApiSequenceConfig, ApiSequenceResult } from '@chatsdet/types';
import type { AgentEvent, AgentEventType } from '../../../../../hooks/useApiSequenceGenerationStream';

type DoctorStatus = 'idle' | 'running' | 'complete' | 'error';

type DoctorInput = {
  propertyId: string;
  credentialId: string | null;
  currentConfig: ApiSequenceConfig;
  failedResult: ApiSequenceResult;
  workflowVariables?: Array<{ name: string; source: string; value?: string | null }>;
  mode?: 'fix' | 'improve';
  userGuidance?: string;
};

export function useApiSequenceDoctor() {
  const [events, setEvents] = useState<AgentEvent[]>([]);
  const [status, setStatus] = useState<DoctorStatus>('idle');
  const [finalConfig, setFinalConfig] = useState<ApiSequenceConfig | null>(null);
  const [summary, setSummary] = useState<string | null>(null);
  const abortRef = useRef<AbortController | null>(null);

  const doctor = useCallback((input: DoctorInput) => {
    setEvents([]);
    setStatus('running');
    setFinalConfig(null);
    setSummary(null);

    const controller = new AbortController();
    abortRef.current = controller;

    const run = async () => {
      try {
        const response = await fetch(`${API_URL}/api-sequence-agent/doctor`, {
          method: 'POST',
          credentials: 'include',
          headers: { 'content-type': 'application/json' },
          body: JSON.stringify(input),
          signal: controller.signal,
        });

        if (!response.ok) {
          const contentType = response.headers.get('content-type') ?? '';
          let message: string | null = null;

          if (contentType.includes('application/json')) {
            try {
              const payload = (await response.json()) as { error?: unknown };
              if (typeof payload.error === 'string' && payload.error.trim().length > 0) {
                message = payload.error;
              }
            } catch {
              // Fall through
            }
          }

          if (!message) {
            const text = await response.text();
            message = text || `HTTP ${response.status}`;
          }

          setEvents((prev) => [...prev, { type: 'error', timestamp: Date.now(), message }]);
          setStatus('error');
          return;
        }

        const reader = response.body?.getReader();
        if (!reader) {
          setStatus('error');
          return;
        }

        const decoder = new TextDecoder();
        let buffer = '';

        while (true) {
          const { value, done } = await reader.read();
          if (done) break;

          buffer += decoder.decode(value, { stream: true });
          const parts = buffer.split('\n\n');
          buffer = parts.pop() ?? '';

          for (const part of parts) {
            if (!part.trim()) continue;

            let eventType = '';
            let data = '';

            for (const line of part.split('\n')) {
              if (line.startsWith('event: ')) eventType = line.slice(7);
              else if (line.startsWith('data: ')) data = line.slice(6);
            }

            if (!eventType || !data) continue;

            try {
              const parsed = JSON.parse(data);
              const event: AgentEvent = {
                type: eventType as AgentEventType,
                timestamp: Date.now(),
                ...parsed,
              };

              setEvents((prev) => [...prev, event]);

              if (eventType === 'complete') {
                setFinalConfig(parsed.config ?? null);
                setSummary(parsed.summary ?? null);
                setStatus('complete');
              } else if (eventType === 'error') {
                setStatus('error');
              }
            } catch {
              // Ignore malformed event payloads
            }
          }
        }

        setStatus((prev) => (prev === 'running' ? 'complete' : prev));
      } catch (error) {
        if ((error as Error).name === 'AbortError') {
          setStatus('idle');
          return;
        }

        setEvents((prev) => [
          ...prev,
          { type: 'error', timestamp: Date.now(), message: error instanceof Error ? error.message : String(error) },
        ]);
        setStatus('error');
      }
    };

    void run();
  }, []);

  const cancel = useCallback(() => {
    abortRef.current?.abort();
    abortRef.current = null;
    setStatus('idle');
  }, []);

  const reset = useCallback(() => {
    abortRef.current?.abort();
    abortRef.current = null;
    setEvents([]);
    setStatus('idle');
    setFinalConfig(null);
    setSummary(null);
  }, []);

  return { events, status, finalConfig, summary, doctor, cancel, reset };
}
